import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { useAppSelector } from 'hooks/reduxHooks'
import { usePlaceById } from 'hooks/usePlace'
import { isMaldivesDestinationSearch, isMaldivesLocation } from 'lib/offer/isMaldivesHotel'
import { isAnywhereDestinationSearch } from 'lib/search/searchUtils'

interface Options {
  destinationId?: string
  destinationName?: string
  offerIds?: ReadonlyArray<string>
  offerListFetching?: boolean
  propertySpecificSearch?: boolean
}

interface HotelSearchExclusionContext {
  destinationPlace?: App.Place
  isAnywhereSearch: boolean
  isMaldivesSearch: boolean
  isSearchExcluded: boolean
  isExclusionPending: boolean
}

function useHotelSearchExclusionContext({
  destinationId,
  destinationName,
  offerIds = [],
  offerListFetching = false,
  propertySpecificSearch = false,
}: Options): HotelSearchExclusionContext {
  const shouldSkipPlaceLookup = !destinationId || destinationId.includes(',') || destinationId === ANYWHERE_PLACE_ID
  const destinationPlace = usePlaceById(destinationId, shouldSkipPlaceLookup)
  const destinationPlaceLookupPending = !shouldSkipPlaceLookup && !destinationPlace

  const isAnywhereSearch = isAnywhereDestinationSearch(destinationId)
  const isMaldivesDestination = isMaldivesDestinationSearch(destinationId, destinationName, destinationPlace?.countryCode)
  const hasCharterBundleOffers = useAppSelector(state => {
    return offerIds.some(offerId => {
      const offer = state.offer.offers[offerId]
      if (!offer || !('campaignTags' in offer)) return false
      return offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
    })
  })

  const propertySearchOffer = useAppSelector(state => {
    if (!propertySpecificSearch || !offerIds[0]) return undefined
    return state.offer.offers[offerIds[0]]
  })
  const propertyCountry = (() => {
    if (!propertySearchOffer) return undefined

    const property = propertySearchOffer.property as (App.OfferProperty & { address?: App.BedbankPropertyAddress }) | undefined
    return property?.geoData?.countryCode ||
      property?.geoData?.country ||
      property?.address?.countryCode ||
      property?.address?.countryName
  })()
  const propertyCountryMatchesMaldives = propertySpecificSearch && isMaldivesLocation(propertyCountry)
  const propertyCountryLookupPending = propertySpecificSearch && !!offerIds[0] && !propertySearchOffer

  const isMaldivesSearch = isMaldivesDestination || propertyCountryMatchesMaldives || hasCharterBundleOffers
  const isExclusionPending = destinationPlaceLookupPending || offerListFetching || propertyCountryLookupPending

  return {
    destinationPlace,
    isAnywhereSearch,
    isMaldivesSearch,
    isSearchExcluded: isAnywhereSearch || isMaldivesSearch,
    isExclusionPending,
  }
}

export default useHotelSearchExclusionContext
